import * as React from 'react';
import * as intl from 'react-intl-universal';
import { Link } from 'react-router-dom';
import { Menu } from 'antd';
import Icon from 'common/components/IconFont';
import type { MetaRoute, Routes } from '../type';
import { Icons } from './Icons';
import { visualRoutes } from './utils';

const { SubMenu } = Menu;

interface MobileMenuProps {
    roleType: 'Admin' | 'Client' | 'Sales';
    routes: Routes;
    activeKeys: string[];
    openKeys: string[];
    onOpenChange: (keys: string[]) => void;
    onNavigate: () => void;
}

const getMenuText = (roleType: MobileMenuProps['roleType'], key: string) => intl.get(`Menu.${roleType}${key}`);

const renderRoutes = (
    routes: Routes,
    roleType: MobileMenuProps['roleType'],
    onNavigate: MobileMenuProps['onNavigate'],
    onOpenChange: MobileMenuProps['onOpenChange'],
): React.ReactNode[] => {
    const renderItem = (route: MetaRoute, text: string) => (
        <Menu.Item key={route.key}>
            <Link className="ellipsis" title={text} to={route.href} onClick={onNavigate}>
                {text}
            </Link>
        </Menu.Item>
    );

    return visualRoutes(routes || [])
        .map((route: MetaRoute) => {
            const explicitRoutes = visualRoutes(route.children || []);
            const text = getMenuText(roleType, route.key);

            if (!text) return null;

            if (!explicitRoutes.length) {
                return renderItem(route, text);
            }

            return (
                <SubMenu
                    key={route.key}
                    title={
                        <span title={text}>
                            <Icon
                                className={`anticon-${route.type === 'client' ? (route.icon ?? Icons[route.key]) : 'point'} ${route.type === 'client' ? 'subMenu-icon' : ''}`}
                            />
                            {text}
                        </span>
                    }
                    onTitleClick={(event: any) => onOpenChange([event.key])}
                >
                    {explicitRoutes
                        .map((child: MetaRoute) => {
                            const childChildren = visualRoutes(child.children || []);
                            const childText = getMenuText(roleType, child.key);

                            if (!childText) return null;

                            if (!childChildren.length) {
                                return renderItem(child, childText);
                            }

                            return (
                                <SubMenu
                                    key={child.key}
                                    title={
                                        <span title={childText}>
                                            <Icon
                                                className={`anticon-${child.type === 'client' ? (child.icon ?? Icons[child.key]) : 'point'} ${child.type === 'client' ? 'subMenu-icon' : ''}`}
                                            />
                                            {childText}
                                        </span>
                                    }
                                    onTitleClick={(event: any) => onOpenChange([event.key])}
                                >
                                    {childChildren
                                        .map((grand: MetaRoute) => {
                                            const grandText = getMenuText(roleType, grand.key);
                                            if (!grandText) return null;
                                            return renderItem(grand, grandText);
                                        })
                                        .filter(Boolean)}
                                </SubMenu>
                            );
                        })
                        .filter(Boolean)}
                </SubMenu>
            );
        })
        .filter(Boolean);
};

const Mobile = ({ roleType, routes, activeKeys, openKeys, onOpenChange, onNavigate }: MobileMenuProps) => (
    <Menu
        className="Launcher-mobile-menu"
        mode="inline"
        selectedKeys={activeKeys}
        openKeys={openKeys}
        onOpenChange={onOpenChange}
    >
        {renderRoutes(routes, roleType, onNavigate, onOpenChange)}
    </Menu>
);

export default Mobile;
